/**
 * This file defines the invite() service.
 */

import { AnyAri } from '@atlassian/ari';

import type { AnyServiceWithDeps, DependenciesContext } from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getErrorDetails,
	getStargateURL,
	isAcceptableFailure,
	isAcceptableFailureForUserInvite,
	type Product,
	shouldReportFailedInvite,
} from '../common/common';
import { fetchJSON } from '../common/fetchJSON';

type User = {
	email?: string;
	id?: string;
};

export type InviteResult =
	| (User & {
			integration: string;
			resource: AnyAri;
	  } & (
				| {
						error: null;
						inviteStatus: 'INVITED';
				  }
				| {
						error: null;
						inviteStatus: 'USER_EXISTS';
				  }
				| {
						error: null;
						inviteStatus: 'INVITED_PENDING_APPROVAL';
				  }
				| {
						error: null;
						inviteStatus: 'PENDING_INVITE_EXISTS';
				  }
				| {
						error: null;
						inviteStatus: 'NOT_INVITED';
				  }
				| {
						error: InviteErrorReason;
						inviteStatus: 'ERROR';
				  }
			))
	| {
			error: Error;
			// When entire request fails, for example, bad request, network error, etc.
			// TODO: This can be more specific, but we need to know what the response status is.
			inviteStatus: null;
	  };

export const INVITE_STATUSES = [
	'INVITED',
	'USER_EXISTS',
	'INVITED_PENDING_APPROVAL',
	'PENDING_INVITE_EXISTS',
	'NOT_INVITED',
	'ERROR',
] as const;

export type InviteStatus = (typeof INVITE_STATUSES)[number];

type InviteErrorReason = {
	code: string;
	message: string;
};

export type InviteResponse = (User & {
	errorReasons?: {
		[resource: string]: InviteErrorReason;
	};
	results: {
		[resource: string]: InviteStatus;
	};
})[];

export interface RecaptchaOptions {
	recaptchaAction?: string;
	recaptchaToken?: string;
}

// Input - data directly needed to invite a user/s to a product:
export type InviteInput = {
	continueUrl: string;
	integration: string;
	recaptchaOptions?: RecaptchaOptions;
	resources: AnyAri[];
	users: User[];
};

// Dependencies - generally common to all invites:
const dependencies = {
	fetchJSON,
	createAnalyticsEvent,
	getStargateURL,
	getCurrentUserId,
	getCurrentCloudId,
	getCurrentProduct,
};

type Deps = typeof dependencies;

export const invite = Object.assign(inviteImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function inviteImpl(input: InviteInput, context: DependenciesContext<Deps>) {
	const deps = context.dependencies();
	let results: InviteResult[];
	try {
		// https://developer.atlassian.com/platform/identity/rest/v1/api-group-invitations/#api-v2-invitations-invite-post
		const response = await deps.fetchJSON<InviteResponse>({
			url: deps.getStargateURL('/v3/invitations/invite'),
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(input.recaptchaOptions?.recaptchaToken
					? { 'G-Recaptcha-Token': input.recaptchaOptions.recaptchaToken }
					: {}),
				...(input.recaptchaOptions?.recaptchaAction
					? { 'Recaptcha-Action': input.recaptchaOptions.recaptchaAction }
					: {}),
			},
			body: JSON.stringify({
				users: input.users,
				resources: input.resources.map((resource) => resource.toString()),
				continueUrl: input.continueUrl,
			}),
		});
		results = mapInviteResponse(response, input.integration);
	} catch (error) {
		const { code, message, status } = getErrorDetails(error);
		const attributes: AnalyticsAttributes = {
			code: code,
			message: message,
			status: status,
			integration: input.integration,
		};

		if (!isAcceptableFailure(message, status)) {
			inviteFailedSLO(attributes, context);
		}
		results = [{ inviteStatus: null, error: toError(error) }];
	}

	results.forEach((result) => fireInviteAnalytics(result, context));
	return results;
}

export const mapInviteResponse = (response: InviteResponse, integration: string): InviteResult[] =>
	response.flatMap((user) =>
		Object.keys(user.results).map((resource) => {
			const inviteStatus = user.results[resource];
			const resourceAri = AnyAri.parse(resource);
			const common = {
				id: user?.id,
				email: user?.email,
				integration,
				resource: resourceAri,
			};

			if (inviteStatus === 'ERROR') {
				return {
					...common,
					inviteStatus,
					// errorReasons should always be present for error responses
					error: user.errorReasons?.[resource] ?? {
						code: 'UNKNOWN_ERROR',
						message: 'Unknown error',
					},
				};
			}

			return {
				...common,
				inviteStatus,
				error: null,
			};
		}),
	);

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

interface BulkInviteResult {
	error: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'NOT_INVITED' | 'ERROR' }>,
		'email'
	>[];
	invited: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED' | 'USER_EXISTS' }>,
		'email' | 'id'
	>[];
	requested: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED_PENDING_APPROVAL' | 'PENDING_INVITE_EXISTS' }>,
		'email' | 'id'
	>[];
}

const isInvited = (res: InviteResult): res is BulkInviteResult['invited'][number] => {
	return res.inviteStatus === 'INVITED' || res.inviteStatus === 'USER_EXISTS';
};

const isRequested = (res: InviteResult): res is BulkInviteResult['requested'][number] => {
	return (
		res.inviteStatus === 'INVITED_PENDING_APPROVAL' || res.inviteStatus === 'PENDING_INVITE_EXISTS'
	);
};

const isError = (res: InviteResult): res is BulkInviteResult['error'][number] => {
	return res.inviteStatus === 'NOT_INVITED' || res.inviteStatus === 'ERROR';
};

export const mapInviteResult = (result: InviteResult[]) =>
	result.reduce<BulkInviteResult>(
		(accumulator, res) => {
			switch (res.inviteStatus) {
				case 'INVITED':
				case 'USER_EXISTS':
					isInvited(res) && accumulator.invited.push(res);
					break;
				case 'INVITED_PENDING_APPROVAL':
				case 'PENDING_INVITE_EXISTS':
					isRequested(res) && accumulator.requested.push(res);
					break;
				case 'NOT_INVITED':
				case 'ERROR':
					isError(res) && accumulator.error.push(res);
					break;
				default:
					throw new Error('Unexpected return type from invite user response');
			}
			return accumulator;
		},
		{
			invited: [],
			requested: [],
			error: [],
		},
	);

// Analytics functions for these common events might later become
// exported for standalone use
function fireInviteAnalytics(result: InviteResult, context: DependenciesContext<Deps>) {
	if (!result.error) {
		const attributes: AnalyticsAttributes = {
			// The id is always present for success responses
			userAaid: result.id!,
			actorAaid: context.dependencies().getCurrentUserId(),
			inviteStatus: result.inviteStatus,
			targetProduct: result.resource.resourceOwner as Product,
			targetTenantId: result.resource.resourceId,
			sourceProduct: context.dependencies().getCurrentProduct(),
			sourceTenantId: context.dependencies().getCurrentCloudId(),
			integration: result.integration,
		};

		inviteSucceededSLO({ integration: attributes.integration }, context);

		if (result.inviteStatus === 'INVITED') {
			fireUserInvited(attributes, context);
		}
		if (result.inviteStatus === 'INVITED_PENDING_APPROVAL') {
			fireProductAccessRequested(attributes, context);
		}
	} else if (result.inviteStatus === 'ERROR') {
		const attributes: AnalyticsAttributes = {
			integration: result.integration,
			inviteStatus: result.inviteStatus,
			code: result.error.code,
			message: result.error.message,
		};

		const { code, message, status } = getErrorDetails(result.error);
		const failure = {
			code,
			message,
			status,
		};

		// There should not be a failure SLO event if the error is a user network failure error
		if (!isAcceptableFailureForUserInvite(failure)) {
			if (shouldReportFailedInvite(failure)) {
				inviteFailedSLO(attributes, context);
			} else {
				inviteSucceededSLO({ integration: result.integration }, context);
			}
		}
	}
}

export type AnalyticsAttributes = {
	actorAaid?: string; // AAID of user initiating request (same as above if RA1/3, different if RA2)
	code?: string; // Error code from request response
	integration: string; // Integration being used to invite the user
	inviteStatus?: InviteStatus; // USER_EXISTS, INVITED_PENDING_APPROVAL
	message?: string; // Error message from request response
	recommendationSources?: string[]; // Source of user invited (eg Google, Slack, Microsoft, Collab Graph?)
	sourceProduct?: Product; // Product being initiated from (in case of server event with different )
	sourceTenantId?: string; // TenantID of the product where the request was initiated
	status?: number; // Numerical error status code from request response
	targetProduct?: Product; // Product being requested to
	targetTenantId?: string; // TenantID of the product where the request is for
	userAaid?: string; // AAID of user who the request is for
	userUuid?: string; // For 3P, if we don't have AAID yet: UUID of user who the request is for
	//...originAttributes TODO?
};

function fireProductAccessRequested(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'productAccess',
				action: 'requested',
				actionSubjectId: attributes.targetProduct,
				source: attributes.integration,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
}

function fireUserInvited(attributes: AnalyticsAttributes, context: DependenciesContext<Deps>) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'user',
				action: 'invited',
				actionSubjectId: attributes.userAaid,
				source: attributes.integration,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
}

const inviteFailedSLO = (attributes: AnalyticsAttributes, context: DependenciesContext<Deps>) => {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'sloInviteRequest',
				action: 'failed',
				source: attributes.integration,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
};

const inviteSucceededSLO = (
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) => {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'sloInviteRequest',
				action: 'succeeded',
				source: attributes.integration,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
};
